import { BOOKING_URL, trackBooking } from "@/lib/booking";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Clock, Search } from "lucide-react";
import { BLOG_POSTS, BLOG_CATEGORIES, formatDate } from "@/data/blog";
import { useSeo } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";

const catKn = (key) => BLOG_CATEGORIES.find((c) => c.key === key)?.kn || "";

const PostCard = ({ p }) => (
  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
    className="group flex flex-col overflow-hidden rounded-[16px] border border-[#A8722A]/20 bg-[#FFFDF8] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
    <div className="h-44 w-full overflow-hidden bg-[#F3EADA]">
      <img src={p.img} alt={p.titleEn} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
    </div>
    <div className="flex flex-1 flex-col p-5">
      <span className="self-start rounded-full bg-[#A8722A]/10 px-2.5 py-0.5 font-mukta text-[11px] font-semibold text-[#A8722A]">{p.cat}</span>
      <p className="mt-3 font-baloo text-base text-[#B23A2B]">{p.titleKn}</p>
      <h3 className="mt-0.5 font-eczar text-lg font-semibold leading-snug text-[#3B221B]">{p.titleEn}</h3>
      <p className="mt-2 flex-1 font-mukta text-sm text-[#6B5044] line-clamp-3">{p.excerpt}</p>
      <div className="mt-4 flex items-center justify-between font-mukta text-xs text-[#6B5044]">
        <span>{formatDate(p.date)}</span>
        <span className="inline-flex items-center gap-1"><Clock size={12} /> {p.readMins} min read</span>
      </div>
    </div>
  </Link>
);

const Featured = ({ p }) => (
  <Link to={`/blog/${p.slug}`} data-testid="blog-featured"
    className="group grid overflow-hidden rounded-[20px] border border-[#A8722A]/25 bg-[#3B221B] text-[#FBF6EC] shadow-md md:grid-cols-2">
    <div className="h-56 overflow-hidden md:h-full">
      <img src={p.img} alt={p.titleEn} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
    </div>
    <div className="flex flex-col justify-center p-7 md:p-10">
      <span className="self-start rounded-full bg-[#A8722A] px-3 py-1 font-mukta text-[11px] font-semibold uppercase tracking-wider text-[#FFFDF8]">Featured · {p.cat}</span>
      <p className="mt-5 font-baloo text-lg text-[#E8B86D]">{p.titleKn}</p>
      <h2 className="mt-1 font-eczar text-2xl font-bold leading-tight sm:text-3xl">{p.titleEn}</h2>
      <p className="mt-3 font-mukta text-sm text-[#FBF6EC]/80 md:text-base">{p.excerpt}</p>
      <span className="mt-6 inline-flex items-center gap-1.5 font-mukta text-sm font-semibold text-[#E8B86D] group-hover:gap-3 transition-all">
        Read the article <ArrowRight size={16} />
      </span>
    </div>
  </Link>
);

export default function BlogHub() {
  const [cat, setCat] = useState("All");
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") || "");

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return BLOG_POSTS.filter((p) => (cat === "All" || p.cat === cat) &&
      (!s || p.titleEn.toLowerCase().includes(s) || p.titleKn.includes(s) || p.excerpt.toLowerCase().includes(s) || p.keywords.some((k) => k.includes(s))));
  }, [cat, q]);

  const featured = cat === "All" && !q ? BLOG_POSTS[0] : null;
  const grid = featured ? filtered.slice(1) : filtered;

  useSeo({
    title: "Blog — ಬ್ಲಾಗ್ | Pooja Guides, Muhurta, Samskaras & Purohith Tips for Bengaluru | PurohithConnect",
    description: "Practical guides on Hindu poojas, homas, samskaras, vratas, muhurta and choosing a purohith in Bengaluru — Griha Pravesha muhurta 2026, Satyanarayana samagri list, Upanayana planning, Rudrabhisheka and more.",
    canonicalPath: "/blog",
    jsonLd: { "@context": "https://schema.org", "@type": "Blog", name: "PurohithConnect Blog", url: "https://purohithconnect.com/blog",
      blogPost: BLOG_POSTS.map((p) => ({ "@type": "BlogPosting", headline: p.titleEn, url: `https://purohithconnect.com/blog/${p.slug}`, datePublished: p.date, image: p.img.startsWith("http") ? p.img : `https://purohithconnect.com${p.img}` })) },
  });

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]">
      <SiteHeader />
      <section className="mx-auto max-w-7xl px-4 pt-10 md:px-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="blog-category-filter">
            {["All", ...BLOG_CATEGORIES.map((c) => c.key)].map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`blog-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                title={c === "All" ? "ಎಲ್ಲಾ" : catKn(c)}
                className={`rounded-full border px-4 py-1.5 font-mukta text-sm font-medium transition-colors ${cat === c ? "border-[#B23A2B] bg-[#B23A2B] text-white" : "border-[#A8722A]/30 text-[#6B5044] hover:border-[#A8722A]"}`}>
                {c}
              </button>
            ))}
          </div>
          <label className="relative block md:w-72">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#6B5044]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ಹುಡುಕಿ · Search articles" data-testid="blog-search"
              className="w-full rounded-full border border-[#A8722A]/30 bg-[#FFFDF8] py-2 pl-9 pr-4 font-mukta text-sm text-[#3B221B] placeholder:text-[#6B5044]/70 focus:border-[#A8722A] focus:outline-none focus:ring-2 focus:ring-[#A8722A]/30" />
          </label>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 md:px-8">
        {featured && <Featured p={featured} />}
        {grid.length ? (
          <div className={`grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 ${featured ? "mt-8" : ""}`} data-testid="blog-grid">
            {grid.map((p) => <PostCard key={p.slug} p={p} />)}
          </div>
        ) : (
          <p className="mt-6 font-mukta text-[#6B5044]" data-testid="blog-empty">ಯಾವುದೇ ಲೇಖನ ಸಿಗಲಿಲ್ಲ · No articles match your search.</p>
        )}
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-14 md:px-8">
        <div className="rounded-[20px] border border-[#A8722A]/25 bg-[#F3EADA] p-7 md:p-10" data-testid="blog-cta">
          <p className="font-baloo text-lg text-[#B23A2B]">ಪುರೋಹಿತರು ಬೇಕೇ?</p>
          <h2 className="mt-1 font-eczar text-2xl font-bold">Need a verified purohith in Bengaluru?</h2>
          <p className="mt-2 max-w-2xl font-mukta text-[#6B5044]">PurohithConnect brings you KYC-verified purohiths across Smartha, Madhwa, Srivaishnava and Lingayat traditions. Book in minutes, or browse ceremonies first.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackBooking({ location: "blog-hub" })} className="rounded-full bg-[#B23A2B] px-5 py-2 font-mukta text-sm font-semibold text-[#FFFDF8] hover:bg-[#9a2f22]" data-testid="blog-cta-book">Book a Purohith ↗</a>
            <Link to="/occasions" className="rounded-full border border-[#A8722A]/40 px-5 py-2 font-mukta text-sm font-semibold text-[#3B221B] hover:border-[#A8722A]" data-testid="blog-cta-occasions">Browse occasions</Link>
            <Link to="/festivals" className="rounded-full border border-[#A8722A]/40 px-5 py-2 font-mukta text-sm font-semibold text-[#3B221B] hover:border-[#A8722A]" data-testid="blog-cta-festivals">Festival Calendar 2026</Link>
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
